"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useEffect, useState } from "react";

import { getQueryClient } from "@/shared/query-client";
import { REACT_QUERY_CONFIG } from "@/shared/config";
import { logger } from "@/shared/lib/logger";

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const tUi = useTranslations("Ui");
  const [queryClient] = useState(() => getQueryClient());

  useEffect(() => {
    // Периодическая очистка устаревших запросов для предотвращения утечек памяти
    const cleanupInterval = setInterval(() => {
      const queries = queryClient.getQueryCache().getAll();
      const now = Date.now();
      let removedCount = 0;
      
      queries.forEach((query) => {
        const queryState = query.state;
        if (queryState.dataUpdatedAt && query.state.status !== "pending") {
          const timeSinceUpdate = now - queryState.dataUpdatedAt;
          const gcTime = query.options.gcTime ?? REACT_QUERY_CONFIG.DEFAULT_GC_TIME;
          
          // Удаляем запросы, которые не использовались более чем в 2 раза дольше их gcTime
          if (timeSinceUpdate > gcTime * 2) {
            queryClient.removeQueries({ queryKey: query.queryKey });
            removedCount++;
          }
        }
      });
      
      if (removedCount > 0) {
        logger.debug(tUi("litQueryProvider.ochistkaKeshaReactQuery"), { removedCount });
      }
      
      // Дополнительная проверка: если кэш слишком большой, удаляем самые старые запросы
      const totalQueries = queries.length;
      const maxQueries = 100; // Максимальное количество запросов в кэше
      if (totalQueries > maxQueries) {
        const sortedQueries = queries
          .filter(q => q.state.dataUpdatedAt)
          .sort((a, b) => (a.state.dataUpdatedAt || 0) - (b.state.dataUpdatedAt || 0));
        
        const toRemove = totalQueries - maxQueries;
        sortedQueries.slice(0, toRemove).forEach(query => {
          queryClient.removeQueries({ queryKey: query.queryKey });
        });
        
      }
    }, 5 * 60 * 1000); // Каждые 5 минут

    return () => clearInterval(cleanupInterval);
  }, [queryClient, tUi]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}
